import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Decimal from 'decimal.js';
import * as Sentry from '@sentry/react';
import { ETracker } from 'common/tracker';
import { useLocalStorageState, useLoginInfo } from '@fuxi/eevee-hooks';
import { MenuProps } from '@fuxi/eevee-ui';
import { AccountType, ArtWorkPayload, AuthInfo, QualificationStatus, UploadFileObjType } from 'common/types';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import { ROUTER_PATHNAME, RedirectParams, navToMyInfo } from 'common/router/config';
import { handleLogout as cleanCookie } from 'common/utils';
import { UserRoleType } from 'common/constants';
import commonService from 'common/service';
import { usePermission } from 'common/atoms/permission';
import service from '@/service';

import {
  setChargeAmount,
  setIncomeScore,
  setTotalIncome,
  setTotalConsume,
  selectChargeAmount,
  selectIncomeScore,
  selectUserInfo,
  setUserInfo,
  setUserInfoLoading,
  selectUserRole,
  setUserRole,
  setAccountAmountInfo,
  selectUserInfoLoading,
  selectTotalConsume,
  selectTotalIncome,
} from '@/store/user';

import { eventEmitter } from '@/utils';
import { WsEventTypes } from '@/constants/websocketEventType';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';

export const useUserAccount = () => {
  const { modal } = useAntStaticComponent();
  const { isLogin, handleLogOut } = useLoginInfo();
  const userAccount = useAppSelector(selectUserInfo);
  const userAccountLoading = useAppSelector(selectUserInfoLoading);
  const chargeAmount = useAppSelector(selectChargeAmount);
  const incomeScore = useAppSelector(selectIncomeScore);
  const userRole = useAppSelector(selectUserRole);
  const totalIncome = useAppSelector(selectTotalIncome);
  const totalConsume = useAppSelector(selectTotalConsume);

  const amountInfo = useAppSelector((state) => state.user.accountAmountInfo);
  const [role, setRole] = useLocalStorageState<UserRoleType | undefined>('tiangong-user-role', {
    defaultValue: UserRoleType.Allocator,
  });
  const [artworks, setArtworks] = useState<ArtWorkPayload[]>([]);
  const [authDetail, setAuthDetail] = useState<AuthInfo>();
  const { platformPermission, currentProjectPermission, setCurrentProjectPermission, getPlatformPermissionQuery } =
    usePermission();

  const { confirm } = modal;

  const nav = useNavigate();

  const dispatch = useAppDispatch();

  const logoutMenus: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'logout',
        label: '登出',
        onClick: () => {
          handleLogOut();
          cleanCookie();
        },
      },
    ],
    [],
  );

  const getAccount = async () => {
    try {
      dispatch(setUserInfoLoading(true));
      const { data } = await service.userSetting.getCurrentUserInfo();

      dispatch(setUserInfo(data as AccountType));
      dispatch(setUserInfoLoading(false));
      if (data && data.uniqueId) {
        Sentry.setUser({ username: data?.name });
        ETracker.setConfig({
          uid: data.uniqueId,
          username: data.name,
          dim1: 'pc',
        });
      }
      return data;
    } catch (err) {
      dispatch(setUserInfoLoading(false));
      console.error(err);
      return err?.data;
    }
  };

  const realNameConfirm = (params?: { redirect: RedirectParams }) => {
    confirm({
      title: '未实名认证',
      content: '你还未进行实名认证，请先至【账户设置-实名认证】处完成认证',
      onOk: () => {
        let navParams = {};
        if (params?.redirect) {
          navParams['redirect'] = params.redirect;
        }
        nav(navToMyInfo({ activeTab: 'realNameAuth', realNameAuth: 'true', ...navParams }));
      },
    });
  };

  const showRealNameConfirm = (params?: { redirect: RedirectParams }) => {
    if (!userAccount?.realNameAuthenticated) {
      realNameConfirm(params);
      return true;
    } else return false;
  };

  const showArtistCertifyConfirm = () => {
    if (userAccount?.artistQualificationStatus !== QualificationStatus.Verified) {
      confirm({
        title: '未创作者认证',
        content: '你还未进行创作者认证，请先进行创作者认证',
        onOk: () => {
          nav(`/${ROUTER_PATHNAME.ARTIST_AUTH}`);
        },
      });
      return true;
    } else return false;
  };

  const showBindBankCardConfirm = (params?: { redirect: RedirectParams }) => {
    confirm({
      title: '未绑定银行卡',
      content: '你还未绑定银行卡，请先至个人中心 -> 银行卡，进行绑定',
      onOk: () => {
        let navParams = {};
        if (params?.redirect) {
          navParams['redirect'] = params.redirect;
        }
        nav(navToMyInfo({ activeTab: 'paymentSetting', ...navParams }));
      },
    });
  };

  const updateAccount = async (params: {
    nickName?: string;
    avatar?: Partial<UploadFileObjType>;
    personalProfile?: string;
  }) => {
    const { code, message: msg } = await service.userSetting.update({
      ...params,
      avatar: params.avatar?.fileId ? params.avatar.fileId : params.avatar?.url,
    });
    if (code === 0) {
      dispatch(
        setUserInfo({
          ...(userAccount as AccountType),
          ...params,
          avatar: params.avatar?.url,
        }),
      );
      return { code, msg };
    } else {
      // message.error(msg);
      return { code, msg };
    }
  };

  //获取用户实名认证信息
  const getUserAuthInfo = async () => {
    const { data } = await service.userSetting.getAuthInfo();
    setAuthDetail(data);
    return data;
  };

  //查询账户余额
  const getAccountBalance = async () => {
    try {
      const res = await service.userSetting.getAccountAmountInfo();
      // 充值余额, 剩余透支额度, 总透支额度
      const {
        chargeAmount,
        chargeCreditLimitBalance,
        chargeCreditLimitTotal,
        incomeScore,
        taskBConsume,
        taskBIncome,
        workshopConsume,
        workshopIncome,
        accountWithdrewScore,
        totalTax,
      } = res.data;
      //账户额度 = 充值余额 + (剩余透支额度 - 总透支额度) , 只保留两位小数
      const amount = Decimal.add(chargeAmount, Decimal.sub(chargeCreditLimitBalance, chargeCreditLimitTotal));
      dispatch(setAccountAmountInfo(res.data));
      dispatch(setChargeAmount(amount.toNumber()));
      dispatch(setIncomeScore(incomeScore));
      const totalIncome = Decimal.add(taskBIncome, workshopIncome).toNumber();
      const totalConsume = Decimal.add(taskBConsume, workshopConsume).toNumber();
      dispatch(setTotalIncome(totalIncome));
      dispatch(setTotalConsume(totalConsume));
      return res.data;
    } catch (error) {}
  };

  const updateRole = async (role: UserRoleType) => {
    dispatch(setUserRole(role));
  };
  useEffect(() => {
    let curRole = userRole;
    if (userRole !== UserRoleType.Allocator && userRole !== UserRoleType.External) {
      curRole = UserRoleType.Allocator;
    }
    updateRole(curRole);
    setRole(curRole);
  }, [userRole]);

  const getUserArtworkList = async () => {
    const data = await commonService.artist.getArtistArtworkList();
    setArtworks(data.items);
    return data;
  };

  useEffect(() => {
    eventEmitter.on(WsEventTypes.RefreshAuthInfo, getAccount);
    return () => {
      eventEmitter.off(WsEventTypes.RefreshAuthInfo, getAccount);
    };
  }, []);

  return {
    getAccount,
    userAccount,
    userAccountLoading,
    updateAccount,
    getAccountBalance,
    chargeAmount,
    incomeScore,
    showRealNameConfirm,
    showArtistCertifyConfirm,
    showBindBankCardConfirm,
    logoutMenus,
    isLogin,
    role,
    updateRole,
    getUserArtworkList,
    artworks,
    getUserAuthInfo,
    authDetail,
    realNameConfirm,
    platformPermission,
    getPlatformPermissionQuery,
    currentProjectPermission,
    setCurrentProjectPermission,
    totalIncome,
    totalConsume,
    amountInfo,
  };
};
